import { useMemo } from 'react';

import { filterRows } from './approvalQueueModel';
import { getBlackoutSearchText } from './tabs/blackoutDates/blackoutQueue';
import { getBundledExperienceSearchText } from './tabs/bundledExperiences/bundledExperiencesQueue';
import { getCampaignSearchText } from './tabs/campaign/campaignQueue';
import { getDealOptionsInclusionsSearchText } from './tabs/dealOptionsInclusions/dealOptionsInclusionsQueue';
import { getDiscountsMarginSearchText } from './tabs/discountsMargin/discountsMarginQueue';
import { getAgeCoverageSearchText, getCapacitySearchText, getMarginSearchText } from './tabs/egs/egsQueue';
import { getRoomSearchText } from './tabs/rooms/roomsQueue';
import { getSetupSearchText } from './tabs/setup/setupQueue';
import { getSurchargeTableSearchText, getSurchargesSearchText } from './tabs/surcharges/surchargesQueue';
import { getTaxesPaymentsSearchText } from './tabs/taxesPayments/taxesPaymentsQueue';
import type { ChecklistCategoryId, ChecklistRowsByCategory } from './types';

function countMatches<Row>(rows: Array<Row>, searchQuery: string, text: (row: Row) => string) {
  return filterRows({
    isApproved: () => false,
    rows,
    searchQuery,
    showAutoApproved: true,
    text,
  }).length;
}

export function computeSearchMatchCounts(
  rows: ChecklistRowsByCategory,
  searchQuery: string,
): Record<ChecklistCategoryId, number> | undefined {
  if (!searchQuery.trim()) {
    return undefined;
  }

  return {
    setup: countMatches(rows.setupRows, searchQuery, getSetupSearchText),
    campaign: countMatches(rows.campaignRows, searchQuery, getCampaignSearchText),
    discountsMargin: countMatches(rows.discountsMarginRows, searchQuery, getDiscountsMarginSearchText),
    taxesPayments: countMatches(rows.taxesPaymentsRows, searchQuery, getTaxesPaymentsSearchText),
    dealOptionsInclusions: countMatches(
      rows.dealOptionsInclusionsRows,
      searchQuery,
      getDealOptionsInclusionsSearchText,
    ),
    rooms: countMatches(rows.roomRows, searchQuery, getRoomSearchText),
    surcharges:
      countMatches(rows.baseSurchargesRows, searchQuery, getSurchargesSearchText) +
      countMatches(rows.surchargeTableRows, searchQuery, getSurchargeTableSearchText),
    blackoutDates: countMatches(rows.blackoutRows, searchQuery, getBlackoutSearchText),
    egs:
      countMatches(rows.marginRows, searchQuery, getMarginSearchText) +
      countMatches(rows.capacityRows, searchQuery, getCapacitySearchText) +
      countMatches(rows.egsAgeCoverageRows, searchQuery, getAgeCoverageSearchText),
    bundledExperiences: countMatches(rows.bundledExperienceRows, searchQuery, getBundledExperienceSearchText),
  };
}

export function useSearchMatchCounts(
  rows: ChecklistRowsByCategory,
  searchQuery: string,
): Record<ChecklistCategoryId, number> | undefined {
  return useMemo(() => computeSearchMatchCounts(rows, searchQuery), [rows, searchQuery]);
}
